import { useContext, useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import moment from "moment";
import dayjs from "dayjs";

import ThemeContext from "../lib/theme-context";

import EnhancedImageLightbox from "../components/EnhancedImageLightbox";
import Tooltip from "@mui/material/Tooltip";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import useWindowDimensions from "../utils/width";

const imgStyle = {
  width: "100%",
  height: "430px",
  objectFit: "cover",
  borderRadius: "16px",
  cursor: "pointer",
  transition: "transform 0.3s ease, box-shadow 0.3s ease",
};

const SharedForecast = () => {
  const { width } = useWindowDimensions();
  const theme = useContext(ThemeContext);

  const [forecast, setForecast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [currentImage, setCurrentImage] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);

  // Additional state variables from single-forecasts.js
  const [pair, setPair] = useState("");
  const [direction, setDirection] = useState("");
  const [date, setDate] = useState(dayjs(new Date()));
  const [sequence, setSequence] = useState(undefined);
  const [positioning, setPositioning] = useState(undefined);
  const [grade, setGrade] = useState(undefined);
  const [sections, setSections] = useState([]);

  const openImageViewer = useCallback((index) => {
    setCurrentImage(index);
    setIsViewerOpen(true);
  }, []);

  const closeImageViewer = () => {
    setCurrentImage(0);
    setIsViewerOpen(false);
  };

  // Helper function to check if pair is USD-related
  const isUSDRelatedPair = (pairName) => {
    if (!pairName) return false;
    const usdPairs = [
      "AUDUSD",
      "GBPUSD",
      "EURUSD",
      "NZDUSD",
      "USDCHF",
      "USDJPY",
      "USDCAD",
      "XAUUSD",
      "XAGUSD",
      "BTCUSD",
      "SPX500",
    ];
    return usdPairs.includes(pairName);
  };

  useEffect(() => {
    const fetchSharedForecast = async () => {
      try {
        const pathParts = window.location.pathname.split("/");
        const pair = pathParts[2]; // /forecast/pair/direction/dateSlug
        const direction = pathParts[3];
        const dateSlug = pathParts[4];

        const apiUrl = `${process.env.REACT_APP_API}/shared-forecast/${pair}/${direction}/${dateSlug}`;

        const response = await fetch(apiUrl);

        if (response.ok) {
          const data = await response.json();
          setForecast(data);

          // Populate all state variables from the forecast data
          setPair(data.pair);
          setDirection(data.direction);
          setDate(data.date);
          setSequence(data.sequence);
          setPositioning(data.positioning);
          setGrade(data.grade);

          if (data?.sections && data.sections.length > 0) {
            setSections(
              data.sections.map((s, i) => ({
                timeframeName: s.timeframeName || "",
                screenshotLink: s.screenshotLink || "",
                dxyScreenshotLink: s.dxyScreenshotLink || "",
                description: s.description || "",
                afterScreenshotLink:
                  s.afterScreenshotLink ||
                  (i === 0 ? data.dailyLinkAfter : "") ||
                  (i === 1 ? data.hourlyLinkAfter : "") ||
                  "",
                dxyAfterScreenshotLink: s.dxyAfterScreenshotLink || "",
                afterDescription:
                  s.afterDescription ||
                  (i === 0 ? data.dailyDescrtpitonAfter : "") ||
                  (i === 1 ? data.hourlyDescrtpitonAfter : "") ||
                  "",
              })),
            );
          } else {
            setSections([
              {
                timeframeName: data.higherTimeframeName || "",
                screenshotLink: data.dailyLink || "",
                description: data.dailyDescrtpiton || "",
                afterScreenshotLink: data.dailyLinkAfter || "",
                afterDescription: data.dailyDescrtpitonAfter || "",
              },
              {
                timeframeName: data.lowerTimeframeName || "",
                screenshotLink: data.hourlyLink || "",
                description: data.hourlyDescrtpiton || "",
                afterScreenshotLink: data.hourlyLinkAfter || "",
                afterDescription: data.hourlyDescrtpitonAfter || "",
              },
            ]);
          }
        } else {
          const errorMessage = await response.text();
          if (response.status === 404) {
            setError("Forecast not found");
          } else {
            setError(errorMessage || "Error fetching forecast");
          }
        }
      } catch (err) {
        console.error("Error fetching shared forecast:", err);
        setError("Error fetching forecast");
      } finally {
        setLoading(false);
      }
    };

    fetchSharedForecast();
  }, []);

  const urls = [
    ...sections.map((s) => s.screenshotLink),
    ...sections.map((s) => s.afterScreenshotLink),
  ];

  useEffect(() => {
    document.onkeydown = checkKey;
    function checkKey(e) {
      if (e.keyCode === 39) {
        closeImageViewer(0);
      }
      if (e.keyCode === 37) {
        closeImageViewer(0);
      }
      if (e.keyCode === 76) {
        closeImageViewer(0);
      }
      if (e.keyCode === 72) {
        closeImageViewer(0);
      }
    }
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div style={{ color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e" }}>
          Loading forecast...
        </div>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        }}
      >
        <div
          style={{
            color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
            textAlign: "center",
            padding: "40px",
            maxWidth: "500px",
          }}
        >
          <ErrorOutlineIcon
            sx={{
              fontSize: "80px",
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              marginBottom: "20px",
            }}
          />
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "600",
              marginBottom: "12px",
              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
            }}
          >
            Forecast Not Found
          </h2>
          <p
            style={{
              fontSize: "16px",
              lineHeight: "1.6",
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
            }}
          >
            This forecast may not exist or has been deactivated.
          </p>
        </div>
      </Box>
    );
  }

  if (!forecast) {
    return null;
  }

  return (
    <Box
      sx={{
        overflowY: !isViewerOpen ? "scroll" : null,
        overflowX: !isViewerOpen ? "hidden" : null,
        position: !isViewerOpen ? "absolute" : "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        color: theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
        width: "100%",
        maxWidth: "1920px",
        height: "100%",
        backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#f9fafb",
        padding: "0px 15px 15px 15px",
        margin: "0 auto",
      }}
    >
      {isViewerOpen && (
        <EnhancedImageLightbox
          images={urls}
          labels={[
            ...sections.map((s) => `${s.timeframeName || "Timeframe"} Before`),
            ...sections.map((s) => `${s.timeframeName || "Timeframe"} After`),
          ]}
          currentIndex={currentImage}
          onClose={closeImageViewer}
          onNavigate={setCurrentImage}
        />
      )}

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          margin: width <= 800 ? "1em 1.5em 0 1.5em" : "1em 2em 0 2em",
          gap: "1em",
        }}
      ></div>

      <Grid
        container
        justifyContent="space-evenly"
        alignItems="center"
        sx={{ minHeight: "100%" }}
      >
        <Grid
          item
          xs={12}
          sx={{ color: theme[0] === "dark" ? "#FCFCFF" : "black" }}
        >
          <div
            style={{
              maxWidth: "95%",
              margin: width <= 768 ? "0.5em auto" : "0.5em auto",
              padding: width <= 768 ? "16px 20px" : "20px 32px",
              borderRadius: "16px",
            }}
          >
            <div
              style={{
                padding: "16px",
                margin: "16px 0",
                borderRadius: "12px",
                backgroundColor:
                  theme[0] === "dark"
                    ? "rgba(45, 46, 51, 0.4)"
                    : "rgba(249, 250, 251, 0.8)",
                border: `1px solid ${
                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                }`,
                boxShadow:
                  theme[0] === "dark"
                    ? "0 2px 8px rgba(0, 0, 0, 0.3)"
                    : "0 2px 8px rgba(0, 0, 0, 0.05)",
              }}
            >
              {width <= 768 ? (
                <>
                  {pair && (
                    <div style={{ textAlign: "center", marginBottom: "12px" }}>
                      <span
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          letterSpacing: "0.5px",
                          color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                        }}
                      >
                        {pair}
                      </span>
                    </div>
                  )}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "500",
                        letterSpacing: "0.3px",
                        color:
                          theme[0] === "dark"
                            ? "rgba(252, 252, 255, 0.7)"
                            : "rgba(26, 27, 30, 0.7)",
                      }}
                    >
                      {date && moment(date).format("dddd Do YYYY")}
                    </span>
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        letterSpacing: "0.3px",
                        color:
                          theme[0] === "dark"
                            ? "rgba(252, 252, 255, 0.5)"
                            : "rgba(26, 27, 30, 0.5)",
                      }}
                    >
                      Forecast
                    </span>
                  </div>
                </>
              ) : (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "12px",
                    flexWrap: "wrap",
                  }}
                >
                  {pair && (
                    <span
                      style={{
                        fontSize: "23px",
                        fontWeight: "600",
                        letterSpacing: "0.5px",
                        color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                      }}
                    >
                      {pair}
                    </span>
                  )}

                  <div
                    style={{
                      display: "inline-block",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "14px",
                        fontWeight: "700",
                        letterSpacing: "0.5px",
                        padding: "3px 8px",
                        borderRadius: "6px",
                        backgroundColor:
                          direction === "Short"
                            ? theme[0] === "dark"
                              ? "rgba(219, 6, 113, 0.15)"
                              : "rgba(219, 6, 113, 0.1)"
                            : direction === "Long"
                              ? theme[0] === "dark"
                                ? "rgba(38, 166, 93, 0.15)"
                                : "rgba(38, 166, 93, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(237, 158, 14, 0.15)"
                                : "rgba(237, 158, 14, 0.1)",
                        border: `2px solid ${
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E"
                        }`,
                        color:
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E",
                        display: "inline-block",
                      }}
                    >
                      {direction}
                    </span>
                  </div>

                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "500",
                      letterSpacing: "0.3px",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.7)"
                          : "rgba(26, 27, 30, 0.7)",
                    }}
                  >
                    {date && moment(date).format("dddd Do YYYY")}
                  </span>

                  <span
                    style={{
                      fontSize: "18px",
                      fontWeight: "400",
                      letterSpacing: "0.3px",
                      color:
                        theme[0] === "dark"
                          ? "rgba(252, 252, 255, 0.5)"
                          : "rgba(26, 27, 30, 0.5)",
                    }}
                  >
                    Forecast
                  </span>
                </div>
              )}

              <hr
                style={{
                  width: "100%",
                  border: "none",
                  borderTop: `1px solid ${
                    theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                  }`,
                  margin: "16px 0",
                }}
              />

              <div
                style={{
                  display: width < 768 ? "grid" : "flex",
                  gridTemplateColumns: width < 768 ? "1fr 1fr" : undefined,
                  flexDirection: width < 768 ? undefined : "row",
                  justifyContent: width < 768 ? "center" : "space-evenly",
                  alignItems: "center",
                  gap: width < 768 ? "12px" : "20px",
                  flexWrap: "wrap",
                }}
              >
                {width < 768 && direction && (
                  <div
                    style={{
                      position: "relative",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      backgroundColor:
                        direction === "Short"
                          ? theme[0] === "dark"
                            ? "rgba(219, 6, 113, 0.15)"
                            : "rgba(219, 6, 113, 0.1)"
                          : direction === "Long"
                            ? theme[0] === "dark"
                              ? "rgba(38, 166, 93, 0.15)"
                              : "rgba(38, 166, 93, 0.1)"
                            : theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.15)"
                              : "rgba(237, 158, 14, 0.1)",
                      border: `2px solid ${
                        direction === "Short"
                          ? "#db0671"
                          : direction === "Long"
                            ? "#26a65d"
                            : "#ED9E0E"
                      }`,
                      transition: "all 0.3s ease",
                      minWidth: width < 768 ? "100px" : "120px",
                      maxWidth: width < 768 ? "140px" : "160px",
                      textAlign: "center",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "8px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 2px 0",
                      }}
                    >
                      DIRECTION
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          direction === "Short"
                            ? "#db0671"
                            : direction === "Long"
                              ? "#26a65d"
                              : "#ED9E0E",
                      }}
                    >
                      {direction.toUpperCase()}
                    </p>
                  </div>
                )}
                {positioning && (
                  <div
                    style={{
                      position: "relative",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      backgroundColor:
                        positioning === "Excellent Positioning"
                          ? theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : positioning === "Average Positioning"
                            ? theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.15)"
                              : "rgba(237, 158, 14, 0.1)"
                            : theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)",
                      border: `2px solid ${
                        positioning === "Excellent Positioning"
                          ? "#26a65d"
                          : positioning === "Average Positioning"
                            ? "#ED9E0E"
                            : "#f23645"
                      }`,
                      transition: "all 0.3s ease",
                      minWidth: width < 768 ? "100px" : "120px",
                      maxWidth: width < 768 ? "140px" : "160px",
                      textAlign: "center",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "8px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 2px 0",
                      }}
                    >
                      POSITIONING
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          positioning === "Excellent Positioning"
                            ? "#26a65d"
                            : positioning === "Average Positioning"
                              ? "#ED9E0E"
                              : "#f23645",
                      }}
                    >
                      {positioning.replace(" Positioning", "").toUpperCase()}
                    </p>
                  </div>
                )}

                {sequence && (
                  <div
                    style={{
                      position: "relative",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      backgroundColor:
                        sequence === "Excellent Sequence"
                          ? theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : sequence === "Average Sequence"
                            ? theme[0] === "dark"
                              ? "rgba(237, 158, 14, 0.15)"
                              : "rgba(237, 158, 14, 0.1)"
                            : theme[0] === "dark"
                              ? "rgba(242, 54, 69, 0.15)"
                              : "rgba(242, 54, 69, 0.1)",
                      border: `2px solid ${
                        sequence === "Excellent Sequence"
                          ? "#26a65d"
                          : sequence === "Average Sequence"
                            ? "#ED9E0E"
                            : "#f23645"
                      }`,
                      transition: "all 0.3s ease",
                      minWidth: width < 768 ? "100px" : "120px",
                      maxWidth: width < 768 ? "140px" : "160px",
                      textAlign: "center",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "8px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 2px 0",
                      }}
                    >
                      SEQUENCE
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          sequence === "Excellent Sequence"
                            ? "#26a65d"
                            : sequence === "Average Sequence"
                              ? "#ED9E0E"
                              : "#f23645",
                      }}
                    >
                      {sequence.replace(" Sequence", "").toUpperCase()}
                    </p>
                  </div>
                )}

                {grade && (
                  <div
                    style={{
                      position: "relative",
                      padding: "4px 10px",
                      borderRadius: "12px",
                      backgroundColor:
                        grade === "Low Risk"
                          ? theme[0] === "dark"
                            ? "rgba(38, 166, 93, 0.15)"
                            : "rgba(38, 166, 93, 0.1)"
                          : grade === "High Risk"
                            ? theme[0] === "dark"
                              ? "rgba(255, 165, 0, 0.15)"
                              : "rgba(255, 165, 0, 0.1)"
                            : grade === "Invalid"
                              ? theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)"
                              : theme[0] === "dark"
                                ? "rgba(242, 54, 69, 0.15)"
                                : "rgba(242, 54, 69, 0.1)",
                      border: `2px solid ${
                        grade === "Low Risk"
                          ? "#26a65d"
                          : grade === "High Risk"
                            ? "#FFA500"
                            : grade === "Invalid"
                              ? "#f23645"
                              : "#f23645"
                      }`,
                      transition: "all 0.3s ease",
                      minWidth: width < 768 ? "100px" : "120px",
                      maxWidth: width < 768 ? "140px" : "160px",
                      textAlign: "center",
                      outline: "none",
                      WebkitTapHighlightColor: "transparent",
                      userSelect: "none",
                    }}
                  >
                    <p
                      style={{
                        fontSize: "8px",
                        fontWeight: "600",
                        letterSpacing: "0.8px",
                        color: theme[0] !== "dark" ? "#6b7280" : "#9ca3af",
                        margin: "0 0 2px 0",
                      }}
                    >
                      RISK GRADE
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        fontWeight: "600",
                        letterSpacing: "0.3px",
                        margin: 0,
                        color:
                          grade === "Low Risk"
                            ? "#26a65d"
                            : grade === "High Risk"
                              ? "#FFA500"
                              : grade === "Invalid"
                                ? "#f23645"
                                : "#f23645",
                      }}
                    >
                      {grade.replace(" Risk", "").toUpperCase()}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Chart Images */}
            {urls.length > 0 && (
              <div style={{ marginTop: "20px" }}>
                <Grid
                  container
                  spacing={0}
                  sx={{ maxWidth: "95%", margin: "0 auto" }}
                >
                  {sections.map((section, index) => (
                    <Grid
                      key={`section-${index}`}
                      item
                      xs={12}
                      md={6}
                      sx={{ padding: { xs: "0", md: "8px" } }}
                    >
                      {/* Before Section */}
                      <div
                        style={{
                          width: "100%",
                          minHeight: "60px",
                          margin: "0 auto",
                        }}
                      >
                        <div
                          style={{
                            cursor: "default",
                            padding: "10px 16px",
                            margin: "0.5em auto 0.3em auto",
                            borderRadius: "12px",
                            backgroundColor:
                              theme[0] === "dark"
                                ? "rgba(45, 46, 51, 0.4)"
                                : "rgba(249, 250, 251, 0.8)",
                            border: `2px solid ${
                              theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                            }`,
                            textAlign: "center",
                            maxWidth: "160px",
                            transition: "all 0.3s ease",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              color:
                                theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                              margin: 0,
                            }}
                          >
                            {section.timeframeName} Before
                          </p>
                        </div>
                      </div>
                      {section.screenshotLink ? (
                        <Tooltip
                          title="Click to open screenshot"
                          placement="top-end"
                        >
                          <img
                            style={imgStyle}
                            src={section.screenshotLink}
                            alt={`${section.timeframeName} Screenshot`}
                            height="auto"
                            onClick={() => openImageViewer(index)}
                          />
                        </Tooltip>
                      ) : null}

                      {section.description && (
                        <div
                          style={{
                            margin: "1em auto 0 auto",
                            padding: "16px",
                            borderRadius: "12px",
                            backgroundColor:
                              theme[0] === "dark"
                                ? "rgba(45, 46, 51, 0.4)"
                                : "rgba(249, 250, 251, 0.8)",
                            border: `1px solid ${
                              theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                            }`,
                          }}
                        >
                          <p
                            style={{
                              fontSize: "16px",
                              lineHeight: "1.5",
                              color:
                                theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                              margin: 0,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {section.description}
                          </p>
                        </div>
                      )}

                      {/* DXY Screenshot Section - Only for USD-related pairs */}
                      {isUSDRelatedPair(pair) &&
                        section.timeframeName &&
                        section.dxyScreenshotLink && (
                          <>
                            <div
                              style={{
                                cursor: "default",
                                padding: "10px 16px",
                                margin: "1.5em auto 0.3em auto",
                                borderRadius: "12px",
                                backgroundColor:
                                  theme[0] === "dark"
                                    ? "rgba(45, 46, 51, 0.4)"
                                    : "rgba(249, 250, 251, 0.8)",
                                border: `2px solid ${
                                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                                }`,
                                textAlign: "center",
                                maxWidth: "160px",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color: "#ED9E0E",
                                  margin: 0,
                                }}
                              >
                                DXY {section.timeframeName}
                              </p>
                            </div>
                            <Tooltip
                              title="Click to open DXY screenshot"
                              placement="top-end"
                            >
                              <img
                                style={imgStyle}
                                src={section.dxyScreenshotLink}
                                alt={`DXY ${section.timeframeName} Screenshot`}
                                height="auto"
                                onClick={() => openImageViewer(index + 1000)}
                              />
                            </Tooltip>
                          </>
                        )}

                      {/* After Section - directly below Before */}
                      {section.afterScreenshotLink && (
                        <>
                          <div
                            style={{
                              width: "100%",
                              minHeight: "60px",
                              margin: "0 auto",
                              marginTop: "2em",
                            }}
                          >
                            <div
                              style={{
                                cursor: "default",
                                padding: "10px 16px",
                                margin: "0.5em auto 0.3em auto",
                                borderRadius: "12px",
                                backgroundColor:
                                  theme[0] === "dark"
                                    ? "rgba(156, 163, 175, 0.1)"
                                    : "rgba(107, 114, 128, 0.08)",
                                border: `1px solid ${
                                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                                }`,
                                textAlign: "center",
                                maxWidth: "160px",
                                transition: "all 0.3s ease",
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "16px",
                                  fontWeight: "700",
                                  color:
                                    theme[0] !== "dark" ? "#1a1b1e" : "#FCFCFF",
                                  margin: 0,
                                }}
                              >
                                {section.timeframeName} After
                              </p>
                            </div>
                          </div>
                          <Tooltip
                            title="Click to open screenshot"
                            placement="top-end"
                          >
                            <img
                              style={imgStyle}
                              src={section.afterScreenshotLink}
                              alt={`${section.timeframeName} Screenshot After`}
                              height="auto"
                              onClick={() =>
                                openImageViewer(sections.length + index)
                              }
                            />
                          </Tooltip>

                          {section.afterDescription && (
                            <div
                              style={{
                                margin: "1em auto 0 auto",
                                padding: "16px",
                                borderRadius: "12px",
                                backgroundColor:
                                  theme[0] === "dark"
                                    ? "rgba(45, 46, 51, 0.4)"
                                    : "rgba(249, 250, 251, 0.8)",
                                border: `1px solid ${
                                  theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"
                                }`,
                              }}
                            >
                              <p
                                style={{
                                  fontSize: "16px",
                                  lineHeight: "1.5",
                                  color:
                                    theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                                  margin: 0,
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {section.afterDescription}
                              </p>
                            </div>
                          )}

                          {/* DXY After Screenshot Section - Only for USD-related pairs */}
                          {isUSDRelatedPair(pair) &&
                            section.timeframeName &&
                            section.dxyAfterScreenshotLink && (
                              <>
                                <div
                                  style={{
                                    cursor: "default",
                                    padding: "10px 16px",
                                    margin: "1.5em auto 0.3em auto",
                                    borderRadius: "12px",
                                    backgroundColor:
                                      theme[0] === "dark"
                                        ? "rgba(45, 46, 51, 0.4)"
                                        : "rgba(249, 250, 251, 0.8)",
                                    border: `2px solid ${
                                      theme[0] === "dark"
                                        ? "#2d2e33"
                                        : "#e5e7eb"
                                    }`,
                                    textAlign: "center",
                                    maxWidth: "200px",
                                  }}
                                >
                                  <p
                                    style={{
                                      fontSize: "16px",
                                      fontWeight: "700",
                                      color: "#ED9E0E",
                                      margin: 0,
                                    }}
                                  >
                                    DXY {section.timeframeName} After
                                  </p>
                                </div>
                                <Tooltip
                                  title="Click to open DXY after screenshot"
                                  placement="top-end"
                                >
                                  <img
                                    style={imgStyle}
                                    src={section.dxyAfterScreenshotLink}
                                    alt={`DXY ${section.timeframeName} Screenshot After`}
                                    height="auto"
                                    onClick={() =>
                                      openImageViewer(
                                        sections.length * 2 + index + 1000,
                                      )
                                    }
                                  />
                                </Tooltip>
                              </>
                            )}
                        </>
                      )}
                    </Grid>
                  ))}
                </Grid>
              </div>
            )}
          </div>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SharedForecast;
